import React, { useState, useRef, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { SectionTitle } from '../title/SectionTitle'
import OrderProductItem from './OrderProductItem'
import OrderCouponModal from './OrderCouponModal'
import OrderCartCouponSection from './OrderCartCouponSection'
import Hr8 from '../hr/Hr8'
import { OrderProductWithUsable } from './NewOrder'
import {
  GetOrderSheetsOrderSheetNoCouponsResponse,
  OrderProduct,
  OrderProductOption,
  OrderSheetCoupon,
  PaymentInfo,
} from '@shopby/shop-sdk'
import { postApplyCoupons } from '../../api/coupons'
import { useOrderContext } from '../provider/OrderProvider'

export type ProductCoupon = NonNullable<
  GetOrderSheetsOrderSheetNoCouponsResponse['products'][number]['productCoupons']
>[number]

type SummaryInfo = {
  first: FlattenOrderProduct
  totalCount: number
  groupTotal: number
  groupCouponDiscount: number
  groupFinalPrice: number
} | null

export type FlattenOrderProduct = OrderProduct & {
  orderProductOption: OrderProductOption
  orderProductOptions: [OrderProductOption] // 항상 1개만 들어감
}

export type FlattenOrderProductWithCoupon = FlattenOrderProduct & {
  coupon: ProductCoupon | null
}

type OrderProductSectionProps = {
  orderItems: OrderProductWithUsable[]
  paymentInfo: PaymentInfo
  coupons?: GetOrderSheetsOrderSheetNoCouponsResponse | null
  onReady?: (isReady: boolean) => void
  onChange?: false | (() => void)
}

interface CouponPayload {
  productCoupons: { couponIssueNo: number; productNo: number }[]
  cartCouponIssueNo?: number
}

const OrderProductSection = forwardRef(
  ({ orderItems, paymentInfo, coupons, onReady, onChange }: OrderProductSectionProps, ref) => {
    const [isOpen, setIsOpen] = useState(false) // 상품 섹션
    const [flattenItems, setFlattenItems] = useState<FlattenOrderProduct[]>([])
    const [isModal, setIsModal] = useState<boolean>(false)
    const [modalCouponList, setModalCouponList] = useState<ProductCoupon[]>([])
    const [selectedProductNo, setSelectedProductNo] = useState<number | null>(null)
    const [selectedOptionNo, setSelectedOptionNo] = useState<number | null>(null)

    const isAutoApplied = useRef(false)

    const param = useParams()

    const { productCoupon, setProductCoupon, cartCoupon, setCartCoupon } = useOrderContext()

    const flattenOrderItems = (orderItems: OrderProduct[]): FlattenOrderProduct[] => {
      return orderItems.flatMap((item) =>
        item.orderProductOptions.map((option) => ({
          ...item,
          orderProductOption: option, // 옵션을 별도 필드로
          orderProductOptions: [option], // 옵션 배열을 1개짜리로
        }))
      )
    }

    const groupByProductNo = (items: FlattenOrderProduct[]) => {
      return items.reduce<Record<number, FlattenOrderProduct[]>>((acc, item) => {
        ;(acc[item.productNo] = acc[item.productNo] || []).push(item)
        return acc
      }, {})
    }

    const summaryInfo = useMemo(() => {
      if (!flattenItems.length) return null
      const first = flattenItems[0]
      const totalCount = flattenItems.length
      const groupTotal = flattenItems.reduce((sum, item) => sum + (item.orderProductOption.price.buyAmt ?? 0), 0)
      const groupCouponDiscount = flattenItems.reduce((sum, item) => {
        const applied = productCoupon[`${item.productNo}_${item.orderProductOption.optionNo}`]
        return sum + (applied ? Number(applied.couponDiscountAmt || 0) : 0)
      }, 0)
      const groupFinalPrice = groupTotal - groupCouponDiscount
      return {
        first,
        totalCount,
        groupTotal,
        groupCouponDiscount,
        groupFinalPrice,
      }
    }, [flattenItems, productCoupon])

    const removeDuplicatedCoupon = (
      couponsObj: { [key: string]: ProductCoupon | null },
      coupon: ProductCoupon,
      currentKey: string
    ) => {
      const result = { ...couponsObj }
      Object.entries(result).forEach(([k, appliedCoupon]) => {
        if (
          appliedCoupon &&
          appliedCoupon.couponIssueNo === coupon.couponIssueNo &&
          appliedCoupon.couponNo === coupon.couponNo &&
          k !== currentKey
        ) {
          delete result[k]
        }
      })
      return result
    }

    const getProductCouponsPayload = (couponsObj: { [key: string]: ProductCoupon | null }) => {
      const productCouponMap = new Map<number, ProductCoupon>()
      Object.entries(couponsObj)
        .filter(([_, c]) => c)
        .forEach(([k, c]) => {
          const pNo = Number(k.split('_')[0])
          productCouponMap.set(pNo, c!)
        })
      return Array.from(productCouponMap.entries()).map(([productNo, c]) => ({
        couponIssueNo: c.couponIssueNo,
        productNo,
      }))
    }

    const applyCouponHandler = async (productNo: number, optionNo: number, coupon: ProductCoupon | null) => {
      const key = `${productNo}_${optionNo}`
      let nextCoupons = { ...productCoupon }

      // 1. 동일 쿠폰이 이미 다른 상품에 적용되어 있다면 먼저 제거
      if (coupon) nextCoupons = removeDuplicatedCoupon(nextCoupons, coupon, key)

      // 2. 현재 상품에 쿠폰 적용/해제
      if (coupon === null) {
        delete nextCoupons[key]
      } else {
        nextCoupons[key] = coupon
      }

      // 3. 상태 업데이트 (UI 동기화)
      setProductCoupon(productNo, optionNo, coupon)

      // 4. productNo별로 마지막 쿠폰만 남기기 및 API payload 생성
      const productCoupons = getProductCouponsPayload(nextCoupons)

      // 5. API 호출
      if (param.orderSheetNo) {
        try {
          const payload: CouponPayload = { productCoupons }
          if (cartCoupon) payload.cartCouponIssueNo = cartCoupon.couponIssueNo
          await postApplyCoupons(param.orderSheetNo, payload)
          if (!onChange) return
          onChange?.()
        } catch (error) {
          console.error('쿠폰 적용 중 오류 발생:', error)
        }
      }
    }

    const handleApplyCartCoupon = async (coupon: OrderSheetCoupon | null) => {
      setCartCoupon?.(coupon)
      // 상품 쿠폰 payload 생성
      const productCoupons = getProductCouponsPayload(productCoupon)
      const payload: CouponPayload = { productCoupons }

      if (coupon) payload.cartCouponIssueNo = coupon.couponIssueNo
      if (param.orderSheetNo) {
        try {
          await postApplyCoupons(param.orderSheetNo, payload)
          if (!onChange) return
          onChange?.()
        } catch (err) {
          console.error('장바구니 쿠폰 적용 실패:', err)
        }
      }
    }

    const getFastMaxDiscountAssignment = (
      items: FlattenOrderProduct[],
      coupons: GetOrderSheetsOrderSheetNoCouponsResponse
    ): { productNo: number; optionNo: number; coupon: ProductCoupon }[] => {
      // 1. 모든 상품-쿠폰 조합 생성
      const pairs = items.flatMap((item) => {
        const productCoupons = coupons.products.find((p) => p.productNo === item.productNo)?.productCoupons ?? []
        return productCoupons.map((coupon) => ({
          productNo: item.productNo,
          optionNo: item.orderProductOption.optionNo,
          coupon,
          discount: Number(coupon.couponDiscountAmt) || 0,
        }))
      })

      // 2. 할인액 내림차순 정렬
      pairs.sort((a, b) => b.discount - a.discount)

      // 3. 상품/쿠폰 중복 없이 할당
      const assignedProductNos = new Set<number>()
      const assignedCouponKeys = new Set<string>()
      const result: { productNo: number; optionNo: number; coupon: ProductCoupon }[] = []

      for (const { productNo, optionNo, coupon } of pairs) {
        const couponKey = `${coupon.couponIssueNo}_${coupon.couponNo}`
        if (!assignedProductNos.has(productNo) && !assignedCouponKeys.has(couponKey)) {
          result.push({ productNo, optionNo, coupon })
          assignedProductNos.add(productNo)
          assignedCouponKeys.add(couponKey)
        }
      }

      return result
    }

    const getHighestPriceOptions = (items: OrderProduct[]): FlattenOrderProduct[] => {
      const productMap = new Map<number, FlattenOrderProduct>()
      flattenOrderItems(items).forEach((item) => {
        const prev = productMap.get(item.productNo)
        const currPrice = item.orderProductOption.price ?? 0
        const prevPrice = prev?.orderProductOption.price ?? 0
        if (!prev || currPrice > prevPrice) {
          productMap.set(item.productNo, item)
        }
      })
      return Array.from(productMap.values())
    }

    useEffect(() => {
      setFlattenItems(flattenOrderItems(orderItems))
    }, [orderItems])

    useEffect(() => {
      if (!setIsModal) {
        // setSelectedCoupon(null) // 모달이 닫힐 때 선택된 쿠폰 초기화
        setSelectedProductNo(null) // 모달이 닫힐 때 선택된 상품 번호 초기화
        setModalCouponList([]) // 모달이 닫힐 때 쿠폰 목록 초기화
      }
    }, [setIsModal])

    // 1. 최초 1회만 cartCoupon 자동 세팅
    useEffect(() => {
      if (!isAutoApplied.current && coupons && coupons.cartCoupons && coupons.cartCoupons.length > 0 && setCartCoupon) {
        const maxCoupon = coupons.cartCoupons.reduce((prev, curr) =>
          (prev.couponDiscountAmt || 0) > (curr.couponDiscountAmt || 0) ? prev : curr
        )
        setCartCoupon(maxCoupon)
        // 자동 쿠폰 적용은 cartCoupon이 실제로 세팅된 후에 실행
      }
    }, [coupons?.cartCoupons, setCartCoupon])

    // 2. cartCoupon이 세팅된 후 자동 쿠폰 적용 (최초 1회만)
    useEffect(() => {
      const applyAutoCoupons = async () => {
        if (isAutoApplied.current || !coupons) return
        // if (!cartCoupon) return // cartCoupon이 세팅된 후에만 실행

        const highestPriceItems = getHighestPriceOptions(orderItems)
        const couponApplyList = getFastMaxDiscountAssignment(highestPriceItems, coupons)

        // 쿠폰 상태 일괄 세팅
        await Promise.all(
          couponApplyList.map(({ productNo, optionNo, coupon }) => setProductCoupon(productNo, optionNo, coupon))
        )

        // 쿠폰 적용 API 호출
        if (param.orderSheetNo && couponApplyList.length > 0) {
          const productCoupons = couponApplyList.map(({ productNo, coupon }) => ({
            couponIssueNo: coupon.couponIssueNo,
            productNo,
          }))
          const payload: CouponPayload = { productCoupons }
          if (cartCoupon) payload.cartCouponIssueNo = cartCoupon.couponIssueNo

          try {
            await postApplyCoupons(param.orderSheetNo, payload)
            if (!onChange) return
            onChange?.()
          } catch (err) {
            console.error('쿠폰 자동 적용 실패:', err)
          }
        }

        setFlattenItems(flattenOrderItems(orderItems))
        isAutoApplied.current = true // 최초 1회만 실행
      }

      applyAutoCoupons()
    }, [cartCoupon, orderItems, coupons])

    useEffect(() => {
      // 상품이 1개 이상 있으면 준비 완료로 판단 (필요시 조건 추가)
      const isReady = flattenItems.length > 0
      onReady?.(isReady)
    }, [flattenItems, onReady])

    useImperativeHandle(ref, () => ({
      getCoupons: () => ({
        productCoupon,
        cartCoupon,
      }),
    }))

    const OrderAccordionHeader = ({
      summaryInfo,
      isOpen,
      onToggle,
    }: // cartCoupon,
    {
      summaryInfo: SummaryInfo
      isOpen: boolean
      onToggle: () => void
      cartCoupon?: OrderSheetCoupon | null
    }) => {
      if (!summaryInfo) return null

      // NOTE: 장바구니 쿠폰 추후 적용시..
      // const cartCouponDiscount = cartCoupon?.couponDiscountAmt ? Number(cartCoupon.couponDiscountAmt) : 0
      const totalImmediateDiscount =
        (paymentInfo?.totalImmediateDiscountAmt ?? 0) + (paymentInfo?.totalAdditionalDiscountAmt ?? 0)

      // NOTE: 장바구니 쿠폰 추후 적용시..
      // const totalFinalPrice = paymentInfo.totalStandardAmt - totalImmediateDiscount - summaryInfo.groupCouponDiscount - cartCouponDiscount
      const totalFinalPrice = paymentInfo.totalStandardAmt - totalImmediateDiscount - summaryInfo.groupCouponDiscount

      return (
        <AccordionHeader $isOpen={isOpen} onClick={onToggle}>
          <HeaderMain>
            <HeaderLeft>
              <ProductImage src={summaryInfo.first.imageUrl ?? '/no-image.png'} alt={summaryInfo.first.productName} />
              <ProductTitleWrapper>
                <ProductTitle>{summaryInfo.first.productName}</ProductTitle>
                {summaryInfo.totalCount > 1 && <ProductCount>외 {summaryInfo.totalCount - 1}개</ProductCount>}
              </ProductTitleWrapper>
            </HeaderLeft>
          </HeaderMain>
          <HeaderRight>
            <Row>
              <Label $color='#3a4756'>상품 합계</Label>
              <Value>{paymentInfo.totalStandardAmt.toLocaleString()}원</Value>
            </Row>
            {totalImmediateDiscount > 0 && (
              <Row>
                <Label $color='#43a047'>즉시 할인</Label>
                <Value $color='#43a047'>-{totalImmediateDiscount.toLocaleString()}원</Value>
              </Row>
            )}
            {summaryInfo?.groupCouponDiscount > 0 && (
              <Row>
                <Label $color='#1976d2'>상품 쿠폰</Label>
                <Value $color='#1976d2'>-{summaryInfo.groupCouponDiscount.toLocaleString()}원</Value>
              </Row>
            )}
            {/* 장바구니 쿠폰은 이슈가 있어 우선 주석처리하였습니다. 추후 정상 작동 시 사용 해주세요 */}
            {/* {cartCoupon && cartCouponDiscount > 0 && (
              <Row>
                <Label $color='#1976d2'>장바구니 쿠폰</Label>
                <Value $color='#1976d2'>-{cartCouponDiscount.toLocaleString()}원</Value>
              </Row>
            )} */}
            <TotalRow>
              <Label $color='#0d47a1' style={{ fontWeight: 700 }}>
                총합
              </Label>
              <Value $color='#0d47a1' style={{ fontWeight: 700, fontSize: 17 }}>
                {totalFinalPrice.toLocaleString()}원
              </Value>
            </TotalRow>
          </HeaderRight>
        </AccordionHeader>
      )
    }

    const renderOrderProductGroups = (
      flattenItems: FlattenOrderProduct[],
      coupons: GetOrderSheetsOrderSheetNoCouponsResponse,
      productCoupon: { [key: string]: ProductCoupon | null },
      setModalCouponList: (list: ProductCoupon[]) => void,
      setSelectedProductNo: (no: number) => void,
      setSelectedOptionNo: (no: number) => void,
      setIsModal: (open: boolean) => void
    ) => {
      return Object.entries(groupByProductNo(flattenItems)).map(([productNo, items], idx, arr) => {
        const isAnyOptionCouponApplied = items.some(
          (item) => !!productCoupon[`${item.productNo}_${item.orderProductOption.optionNo}`]
        )
        const couponProduct = coupons.products?.find((p) => p.productNo === Number(productNo))
        const productCoupons = couponProduct?.productCoupons ?? []

        // 그룹 총합 및 쿠폰할인 총액 계산
        const groupTotal = items.reduce((sum, item) => sum + (item.orderProductOption.price.buyAmt ?? 0), 0)
        const groupCouponDiscount = items.reduce((sum, item) => {
          const applied = productCoupon[`${item.productNo}_${item.orderProductOption.optionNo}`]
          return sum + (applied ? Number(applied.couponDiscountAmt || 0) : 0)
        }, 0)
        const groupFinalPrice = groupTotal - groupCouponDiscount

        return (
          <div
            key={productNo}
            className='order_product_group'
            style={{
              // borderBottom: idx < arr.length - 1 ? '1px solid #eee' : 'none',
              borderBottom: '1px solid #eee',
              marginBottom: idx < arr.length - 1 ? 24 : 0,
              paddingBottom: idx < arr.length - 1 ? 24 : 0,
            }}
          >
            {items.map((item) => {
              const isCurrentOptionApplied = !!productCoupon[`${item.productNo}_${item.orderProductOption.optionNo}`]
              const disableCouponButton = isAnyOptionCouponApplied && !isCurrentOptionApplied
              return (
                <OrderProductItem
                  key={item.orderProductOption.optionNo}
                  item={item}
                  productCoupons={productCoupons}
                  disableCouponButton={disableCouponButton}
                  onClickCouponButton={() => {
                    setModalCouponList(productCoupons)
                    setSelectedProductNo(item.productNo)
                    setSelectedOptionNo(item.orderProductOption.optionNo)
                    setIsModal(true)
                  }}
                />
              )
            })}
            {isAnyOptionCouponApplied && (
              <GroupTotalSummary>
                <div className='summary-row'>
                  <div className='col'>
                    <div className='col-content'>
                      <span className='label'>상품합계</span>
                      <span className='value'>{groupTotal.toLocaleString()}원</span>
                    </div>
                  </div>

                  <div className='col'>
                    <div className='sep-container'>
                      <span className='sep'>-</span>
                    </div>
                    <div className='col-content'>
                      <span className='label'>쿠폰할인</span>
                      <span className='value discount'>{groupCouponDiscount.toLocaleString()}원</span>
                    </div>
                  </div>
                  <div className='col'>
                    <div className='sep-container'>
                      <span className='sep'>=</span>
                    </div>
                    <div className='col-content'>
                      <span className='label final-label'>총합</span>
                      <span className='final'>{groupFinalPrice.toLocaleString()}원</span>
                    </div>
                  </div>
                </div>
              </GroupTotalSummary>
            )}
          </div>
        )
      })
    }

    const renderOrderProductGroupsWithoutCoupons = (flattenItems: FlattenOrderProduct[]) => {
      return Object.entries(groupByProductNo(flattenItems)).map(([productNo, items], idx, arr) => (
        <div
          key={productNo}
          className='order_product_group'
          style={{
            borderBottom: idx < arr.length - 1 ? '1px solid #eee' : 'none',
            marginBottom: idx < arr.length - 1 ? 24 : 0,
            paddingBottom: idx < arr.length - 1 ? 24 : 0,
          }}
        >
          {items.map((item) => (
            <OrderProductItem
              key={item.orderProductOption.optionNo}
              item={item}
              productCoupons={[]} // 쿠폰 없음
              disableCouponButton={true} // 쿠폰 버튼 비활성화
              onClickCouponButton={() => {}} // 아무 동작 없음
            />
          ))}
        </div>
      ))
    }

    return (
      <>
        <Section>
          <SectionTitleRow>
            <SectionTitle style={{ margin: 0 }}>주문 상품 내역</SectionTitle>
            <BigArrowBox onClick={() => setIsOpen((prev) => !prev)}>
              {isOpen ? (
                <svg width='40' height='40' viewBox='0 0 40 40'>
                  <polyline
                    points='12,24 20,16 28,24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='3'
                    strokeLinecap='square'
                    strokeLinejoin='miter'
                  />
                </svg>
              ) : (
                <svg width='40' height='40' viewBox='0 0 40 40'>
                  <polyline
                    points='12,16 20,24 28,16'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='3'
                    strokeLinecap='square'
                    strokeLinejoin='miter'
                  />
                </svg>
              )}
            </BigArrowBox>
          </SectionTitleRow>
          {isOpen && (
            <div className='order_item_list'>
              {coupons
                ? renderOrderProductGroups(
                    flattenItems,
                    coupons,
                    productCoupon,
                    setModalCouponList,
                    setSelectedProductNo,
                    setSelectedOptionNo,
                    setIsModal
                  )
                : renderOrderProductGroupsWithoutCoupons(flattenItems)}
            </div>
          )}
          <OrderAccordionHeader
            summaryInfo={summaryInfo}
            isOpen={isOpen}
            onToggle={() => setIsOpen((prev) => !prev)}
            cartCoupon={cartCoupon}
          />
        </Section>
        <Hr8 />

        {coupons && (
          <div className='order_cart_coupon_wrapper'>
            <OrderCartCouponSection cartCoupons={coupons.cartCoupons || []} onApplyCartCoupon={handleApplyCartCoupon} />
          </div>
        )}

        {isModal && (
          <OrderCouponModal
            setIsModal={setIsModal}
            couponList={modalCouponList}
            selectedProductNo={selectedProductNo}
            selectedOptionNo={selectedOptionNo}
            onApplyCoupon={(coupon) => applyCouponHandler(selectedProductNo!, selectedOptionNo!, coupon)}
          />
        )}
      </>
    )
  }
)

OrderProductSection.displayName = 'OrderProductSection'

const Section = styled.div`
  padding: 24px 20px;
  @media screen and (min-width: 768px) {
    padding: 32px 0 32px 0;
    border-bottom: 1px solid var(--colGray3);
    &:last-child {
      border-bottom: none;
    }
  }
`

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 8px;
`

const BigArrowBox = styled.span`
  margin-left: 24px;
  display: flex;
  align-items: center;
  font-size: 40px;
  cursor: pointer;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: #1976d2;
  }

  @media (max-width: 600px) {
    margin-left: 12px;
    font-size: 32px;
  }
`

// 아코디언
const AccordionHeader = styled.div<{ $isOpen: boolean }>`
  cursor: pointer;
  margin-top: ${({ $isOpen }) => ($isOpen ? '32px' : '0px')};
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 20px 14px;
  background: ${({ $isOpen }) => ($isOpen ? '#f8fafc' : '#fff')};
  border-radius: 10px;
  transition: background 0.2s;
  @media (min-width: 601px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`

const HeaderMain = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  @media (max-width: 600px) {
    margin-bottom: 12px;
  }
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
`

const ProductImage = styled.img`
  width: 54px;
  height: 54px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid #e5e8eb;
  background: #fff;
`

const ProductTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 300px;

  @media (max-width: 1225px) {
    max-width: 180px;
  }

  @media screen and (max-width: 1100px) {
    max-width: 60px;
  }

  @media screen and (max-width: 950px) {
    max-width: 220px;
  }
`

const ProductTitle = styled.span`
  font-weight: 600;
  font-size: 16px;
  color: #222;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  max-width: 100%;
  display: block;
`

const ProductCount = styled.span`
  color: #8b95a1;
  margin-left: 10px;
  font-weight: 400;
  font-size: 15px;
  white-space: nowrap;
  min-width: 48px;
  flex-shrink: 0;
  display: inline-block;
`

const HeaderRight = styled.div`
  text-align: right;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 15px;
  align-items: flex-end;
  @media (max-width: 600px) {
    margin-top: 12px;
    width: 100%;
    align-items: flex-end;
  }
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`

const TotalRow = styled(Row)`
  margin-top: 4px;
  border-top: 1px solid #e5e8eb;
  padding-top: 8px;
`

const Label = styled.span<{ $color?: string }>`
  color: ${({ $color }) => $color || '#222'};
  font-size: 15px;
`

const Value = styled.span<{ $color?: string }>`
  font-weight: 700;
  color: ${({ $color }) => $color || '#222'};
  font-size: 15px;
`

// 상품 토탈
const GroupTotalSummary = styled.div`
  margin-top: 16px;
  background: #f5faff;
  border-radius: 8px;
  padding: 14px 28px;
  color: #3a4756;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  .summary-row {
    display: flex;
    align-items: flex-end;
    gap: 2px;
    font-size: 16px;
    font-weight: 500;
    width: 100%;
    justify-content: flex-end;
    flex-wrap: wrap;
  }
  .col {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    min-width: 60px;
    gap: 10px;
    justify-content: flex-end;
    text-align: right;
  }
  .col-content {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: right;
    width: 100%;
  }
  .label {
    color: #3a4756;
    font-weight: 500;
    font-size: 12px;
    margin-bottom: 2px;
    line-height: 1.2;
    text-align: right;
    width: 100%;
  }
  .final-label {
    color: #0d47a1;
    font-weight: 700;
  }
  .value {
    color: #3a4756;
    font-weight: 600;
    font-size: 16px;
    text-align: right;
    width: 100%;
  }
  .discount {
    color: #1976d2;
    font-weight: 700;
  }
  .sep-container {
    display: flex;
    align-items: center;
    min-width: 24px;
    justify-content: flex-end;
  }
  .sep {
    color: #90caf9;
    font-weight: 400;
    font-size: 20px;
    margin: 0;
    align-self: flex-end;
    display: inline-block;
    line-height: 1;
    text-align: right;
  }
  .final {
    color: #0d47a1;
    font-weight: 700;
    font-size: 18px;
    text-align: right;
    width: 100%;
  }

  @media (max-width: 600px) {
    padding: 10px 8px;
    border-radius: 6px;
    .summary-row {
      flex-direction: column;
      align-items: flex-end;
      gap: 0;
      font-size: 15px;
      width: 100%;
    }
    .col {
      min-width: 0;
      margin-bottom: 8px;
      justify-content: flex-end;
      text-align: right;
    }
    .col-content {
      flex-direction: column;
      align-items: flex-end;
      gap: 0;
      width: 100%;
      text-align: right;
    }
    .label,
    .value,
    .final {
      text-align: right;
    }
    .value {
      font-size: 14px;
    }
    .final {
      font-size: 16px;
    }
    .sep-container {
      min-width: 24px;
      margin-bottom: 0px;
    }
    .sep {
      font-size: 18px;
      margin: 0;
      display: inline-block;
      line-height: 1;
      text-align: right;
    }
  }
`

export default OrderProductSection
